import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, Sparkles, Star, ShieldCheck, Clock } from "lucide-react";

/* ─────────────────────────────────────────────
   Simple word-by-word reveal (no variant inheritance traps)
───────────────────────────────────────────── */
function Word({ children, delay = 0, color = "#ffffff", italic = false, gold = false, font }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 22, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{
        display: "inline-block",
        marginRight: "0.28em",
        color: gold ? "transparent" : color,
        fontStyle: italic ? "italic" : "normal",
        fontFamily: font || "'Geist', 'Inter', sans-serif",
        background: gold
          ? "linear-gradient(135deg, #8A8A8A 0%, #AAAAAA 50%, #8A8A8A 100%)"
          : "transparent",
        backgroundSize: gold ? "200% auto" : "auto",
        WebkitBackgroundClip: gold ? "text" : "unset",
        WebkitTextFillColor: gold ? "transparent" : "inherit",
        backgroundClip: gold ? "text" : "unset",
        animation: gold ? "gold-shimmer 6s linear infinite" : "none",
      }}
    >
      {children}
    </motion.span>
  );
}

function Fade({ children, delay = 0, style = {} }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
export default function Hero() {
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const [flash, setFlash] = useState(false);

  const go = (to) => (e) => {
    e.preventDefault();
    setFlash(true);
    setTimeout(() => navigate(to), 380);
  };

  const serif = "'Instrument Serif', 'Fraunces', Georgia, serif";
  const sans  = "'Geist', 'Inter', sans-serif";

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        zIndex: 10,
        paddingTop: 110,
        paddingBottom: 80,
        boxSizing: "border-box",
      }}
    >
      {/* flash overlay */}
      {flash && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.38 }}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "#000", pointerEvents: "none",
          }}
        />
      )}

      {/* ambient warm blobs */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", pointerEvents: "none",
          width: "55vw", height: "55vw",
          top: "-22vw", left: "50%",
          transform: "translateX(-50%)",
          background: "radial-gradient(circle, rgba(138,138,138,0.18) 0%, transparent 60%)",
          filter: "blur(8px)", zIndex: 1,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute", pointerEvents: "none",
          width: "60vw", height: "60vw",
          bottom: "-30vw", left: "50%",
          transform: "translateX(-50%)",
          background: "radial-gradient(circle, rgba(138,138,138,0.10) 0%, transparent 65%)",
          filter: "blur(10px)", zIndex: 1,
        }}
      />

      {/* legibility scrims */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2,
          background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 28%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.80) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2,
          background: "radial-gradient(ellipse 70% 55% at 50% 50%, transparent 0%, rgba(0,0,0,0.30) 70%, rgba(0,0,0,0.80) 100%)",
        }}
      />

      {/* content */}
      <div
        style={{
          position: "relative",
          zIndex: 5,
          width: "100%",
          maxWidth: 1100,
          margin: "0 auto",
          padding: "0 20px",
          textAlign: "center",
        }}
      >
        {/* eyebrow */}
        <Fade delay={0.10}>
          <div
            data-testid="hero-eyebrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 14px",
              background: "rgba(138,138,138,0.10)",
              border: "none",
              borderRadius: 999,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 18px rgba(138,138,138,0.10)",
              marginBottom: 28,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 6, height: 6,
                borderRadius: "50%",
                background: "#8A8A8A",
                boxShadow: "0 0 8px rgba(138,138,138,0.8)",
              }}
            />
            <span
              style={{
                fontFamily: "'Geist Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#f5f5f7",
                fontWeight: 500,
              }}
            >
              Nisky Auto · Niskayuna, NY
            </span>
          </div>
        </Fade>

        {/* headline line 1 — white */}
        <h1
          style={{
            margin: 0,
            fontFamily: sans,
            fontWeight: 600,
            letterSpacing: "-0.05em",
            lineHeight: 0.95,
            fontSize: "clamp(3rem, 11vw, 8rem)",
            color: "#ffffff",
            textShadow: "0 4px 30px rgba(0,0,0,0.7)",
          }}
        >
          <Word delay={0.30}>Showroom</Word>
          <Word delay={0.40}>finish.</Word>
        </h1>

        {/* headline line 2 — gold italic serif */}
        <h1
          style={{
            margin: 0,
            fontFamily: serif,
            fontWeight: 400,
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
            fontSize: "clamp(3rem, 11vw, 8rem)",
            color: "#AAAAAA",
            textShadow: "0 4px 30px rgba(0,0,0,0.7)",
          }}
        >
          <Word delay={0.55} gold italic font={serif}>In</Word>
          <Word delay={0.65} gold italic font={serif}>your</Word>
          <Word delay={0.75} gold italic font={serif}>driveway.</Word>
        </h1>

        {/* gold divider */}
        <Fade delay={0.95}>
          <div
            style={{
              width: 84, height: 1,
              background: "linear-gradient(90deg, transparent, rgba(138,138,138,0.95), transparent)",
              margin: "36px auto 28px",
            }}
          />
        </Fade>

        {/* description */}
        <Fade delay={1.10}>
          <p
            data-testid="hero-description"
            style={{
              color: "rgba(255,255,255,0.94)",
              fontSize: "clamp(15px, 1.6vw, 19px)",
              lineHeight: 1.6,
              maxWidth: 640,
              margin: "0 auto 36px",
              letterSpacing: "-0.01em",
              textShadow: "0 2px 18px rgba(0,0,0,0.8)",
            }}
          >
            Mobile car detailing in Niskayuna, NY. Locally owned, cinematic care
            delivered to your door — June through early September.
          </p>
        </Fade>

        {/* CTAs */}
        <Fade delay={1.25}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: 12,
              marginBottom: 44,
            }}
          >
            <motion.a
              href="/book"
              onClick={go("/book")}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="btn-primary"
              data-testid="hero-book-button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "16px 32px",
                fontSize: 15,
              }}
            >
              <Sparkles size={14} />
              Book a detail
              <ArrowRight size={14} style={{ opacity: 0.7 }} />
            </motion.a>
            <motion.a
              href="/status"
              onClick={go("/status")}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              className="btn-secondary"
              data-testid="hero-status-button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "16px 28px",
                fontSize: 15,
              }}
            >
              <Search size={14} />
              Track your booking
            </motion.a>
          </div>
        </Fade>

        {/* trust row */}
        <Fade delay={1.40}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: 22,
              color: "rgba(255,255,255,0.85)",
              textShadow: "0 1px 12px rgba(0,0,0,0.8)",
            }}
          >
            <TrustItem icon={<Star size={12} fill="#AAAAAA" style={{ color: "#AAAAAA" }} />}>
              5-star rated
            </TrustItem>
            <Divider />
            <TrustItem icon={<ShieldCheck size={12} style={{ color: "#AAAAAA" }} />}>
              Fully insured
            </TrustItem>
            <Divider />
            <TrustItem icon={<Clock size={12} style={{ color: "#AAAAAA" }} />}>
              2–3 hr service
            </TrustItem>
          </div>
        </Fade>
      </div>

      {/* bottom scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        style={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          zIndex: 10,
        }}
      >
        <span
          style={{
            fontSize: 9.5,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.65)",
            textShadow: "0 1px 8px rgba(0,0,0,0.8)",
          }}
        >
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: 4, height: 14, borderRadius: 2,
            background: "linear-gradient(to bottom, rgba(138,138,138,0.95), transparent)",
          }}
        />
      </motion.div>
    </section>
  );
}

function TrustItem({ icon, children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontSize: 12.5,
        letterSpacing: "-0.01em",
      }}
    >
      <span style={{ display: "inline-flex" }}>{icon}</span>
      {children}
    </span>
  );
}

function Divider() {
  return (
    <span
      style={{
        width: 1,
        height: 12,
        background: "rgba(255,255,255,0.22)",
      }}
    />
  );
}
